import * as H from "history";
import { useLocation } from "react-router-dom";
import { LinkedAccount, UnreleasedAPICategory, APICategory } from "../models/Entities";
import { compressToEncodedURIComponent } from "lz-string";
import { serializeFiltersForURL } from "../components/pages/logs/searchbar/SearchBar";
import { APIRequestLogEntryLinkedAccount } from "../../src/components/pages/IntegrationsManagementEntities";

type History = H.History<unknown>;

export type navigateState = {
  fromUpgradeButton?: boolean;
};

export const MERGE_APP_PATH = "https://app.merge.dev";
export const MERGE_APP_EU_PATH = "https://app-eu.merge.dev";
export const MERGE_APP_APAC_PATH = "https://app-ap.merge.dev";
export const ROOT_PATH = "/";
export const LOGIN_PATH = "/login";
export const SIGNUP_PATH = "/signup";
export const LOGIN_WITH_SSO_PATH = "/login/send-sso-url";
export const SAML_NO_ACCOUNT_FOUND = "/sso/saml/no-account-found";
export const PASSWORD_RESET_PATH = "/password/reset";
export const PASSWORD_SEND_RESET_PATH = "/password/send-reset";
export const OAUTH_CALLBACK_PATH = "/oauth/callback";
export const SAML_RELAY_PATH = "/sso/saml/relay";
export const DASHBOARD_PATH = "/dashboard";
export const ONBOARDING_PATH = "/get-started";
export const TOGGLE_SCOPES_PATH = "/toggle-scopes";
export const SYNC_SANDBOX_PATH = `/sync-sandbox-data`;
export const SEND_API_REQUEST_PATH = `/send-api-request`;
export const INTEGRATE_SYNCED_DATA_PATH = `/integrate-synced-data`;
export const ADD_LINK_TO_APP_PATH = `/add-link-to-app`;
export const SYNCING_DATA_PATH = `/syncing-data`;
export const MERGE_WRITES_PATH = `/merge-writes`;
export const WEBHOOKS_PATH = `/webhooks`;
export const METADATA_PATH = `/metadata`;
export const SUPPLEMENTAL_DATA_PATH = `/supplemental-data`;
export const AI_SKILLS_PATH = `/ai-skills`;
export const ONBOARDING_AI_SKILLS_PATH = `${ONBOARDING_PATH}${AI_SKILLS_PATH}`;
export const ONBOARDING_GET_STARTED_TOGGLE_SCOPES_PATH = `${ONBOARDING_PATH}${TOGGLE_SCOPES_PATH}`;
export const ONBOARDING_GET_STARTED_SYNC_SANDBOX_DATA_PATH = `${ONBOARDING_PATH}${SYNC_SANDBOX_PATH}`;
export const ONBOARDING_GET_STARTED_SEND_API_REQUEST_PATH = `${ONBOARDING_PATH}${SEND_API_REQUEST_PATH}`;
export const ONBOARDING_GET_STARTED_INTEGRATE_SYNCED_DATA_PATH = `${ONBOARDING_PATH}${INTEGRATE_SYNCED_DATA_PATH}`;
export const ONBOARDING_GET_STARTED_ADD_LINK_TO_APP_PATH = `${ONBOARDING_PATH}${ADD_LINK_TO_APP_PATH}`;
export const ONBOARDING_ADVANCED_SYNCING_DATA_PATH = `${ONBOARDING_PATH}${SYNCING_DATA_PATH}`;
export const ONBOARDING_ADVANCED_MERGE_WRITES_PATH = `${ONBOARDING_PATH}${MERGE_WRITES_PATH}`;
export const ONBOARDING_ADVANCED_WEBHOOKS_PATH = `${ONBOARDING_PATH}${WEBHOOKS_PATH}`;
export const ONBOARDING_ADVANCED_METADATA_PATH = `${ONBOARDING_PATH}${METADATA_PATH}`;
export const ONBOARDING_ADVANCED_SUPPLEMENTAL_DATA_PATH = `${ONBOARDING_PATH}${SUPPLEMENTAL_DATA_PATH}`;
export const LINKED_ACCOUNTS_PATH = "/linked-accounts";
export const PROD_LINKED_ACCOUNTS_PATH = "/linked-accounts/accounts";
export const PROD_LINKED_ACCOUNT_PATH = "/linked-accounts/account";
export const TEST_LINKED_ACCOUNTS_PATH = "/linked-accounts/test-accounts";
export const LINKED_ACCOUNT_DETAIL_PATH = `${LINKED_ACCOUNTS_PATH}/account`;
export const LINKED_ACCOUNT_DETAIL_ROUTE = `${LINKED_ACCOUNT_DETAIL_PATH}/:id`;
export const LOGS_PATH = "/logs";
export const API_REQUESTS_LOGS_PATH = `${LOGS_PATH}/api-requests`;
export const WEBHOOKS_LOGS_PATH = `${LOGS_PATH}/webhooks`;
export const ISSUES_PATH = "/issues";
export const API_TESTER_PATH = "/api-tester";
export const API_TESTER_MERGE_TO_THIRD_PARTY_PATH = `${API_TESTER_PATH}/merge-to-third-party`;
export const API_TESTER_YOU_TO_MERGE_PATH = `${API_TESTER_PATH}/you-to-merge`;
export const API_TESTER_YOU_TO_MERGE_LINKED_ACCOUNT_PATH = `${API_TESTER_YOU_TO_MERGE_PATH}/linked-account`;
export const API_TESTER_YOU_TO_MERGE_MOCK_SANDBOX_PATH = `${API_TESTER_YOU_TO_MERGE_PATH}/mock-sandbox`;
export const CONFIGURATION_PATH = "/configuration";
export const CONFIGURATION_WEBHOOKS_PATH = `${CONFIGURATION_PATH}/webhooks`;
export const CONFIGURATION_SELECTIVE_SYNC_CONDITION_PRESETS_PATH = `${CONFIGURATION_PATH}/selective-sync`;
export const CONFIGURATION_WEBHOOKS_EMITTERS_PATH = `${CONFIGURATION_WEBHOOKS_PATH}/emitters`;
export const CONFIGURATION_WEBHOOKS_EMITTERS_ADD_PATH = `${CONFIGURATION_WEBHOOKS_EMITTERS_PATH}/add`;
export const CONFIGURATION_WEBHOOKS_RECEIVERS_PATH = `${CONFIGURATION_WEBHOOKS_PATH}/receivers`;
export const CONFIGURATION_FIELD_MAPPINGS_PATH = `${CONFIGURATION_PATH}/field-mappings`;
export const CONFIGURATION_CUSTOM_OBJECTS_PATH = `${CONFIGURATION_PATH}/custom-objects`;
export const CONFIGURATION_DESTINATIONS_PATH = `${CONFIGURATION_PATH}/destinations`;
export const CONFIGURATION_FIELD_MAPPINGS_TARGET_FIELDS_PATH = `${CONFIGURATION_FIELD_MAPPINGS_PATH}/target-fields`;
export const CONFIGURATION_FIELD_MAPPINGS_TARGET_FIELDS_ID_PATH = `${CONFIGURATION_FIELD_MAPPINGS_TARGET_FIELDS_PATH}/:fieldMappingTargetId`;
export const CONFIGURATION_FIELD_MAPPINGS_TARGET_FIELDS_ID_SETTINGS_PATH = `${CONFIGURATION_FIELD_MAPPINGS_PATH}/target-fields/:fieldMappingTargetId/settings`;
export const CONFIGURATION_FIELD_MAPPINGS_COMMON_MODEL_OVERRIDES_PATH = `${CONFIGURATION_FIELD_MAPPINGS_PATH}/common-model-overrides/:id`;
export const CONFIGURATION_FIELD_MAPPINGS_TARGET_FIELDS_INTEGRATION_CREATE_PATH = `${CONFIGURATION_FIELD_MAPPINGS_TARGET_FIELDS_PATH}/:fieldMappingTargetId/:integrationID/create`;
export const CONFIGURATION_FIELD_MAPPINGS_LINKED_ACCOUNTS_PATH = `${CONFIGURATION_FIELD_MAPPINGS_PATH}/linked-accounts`;
export const CONFIGURATION_ADVANCED_PATH = `${CONFIGURATION_PATH}/advanced`;
export const CONFIGURATION_ADVANCED_CUSTOM_FIELDS_PATH = `${CONFIGURATION_PATH}/field-mappings/linked-accounts`;
export const CONFIGURATION_INTEGRATIONS_PATH = `/integrations`;
export const CONFIGURATION_INTEGRATIONS_ENDPOINT_USAGE_PATH = `${CONFIGURATION_INTEGRATIONS_PATH}/endpoint-usage`;
export const CONFIGURATION_COMMON_MODELS_PATH = `/common-models`;
export const CONFIGURATION_ADVANCED_FIELD_MAPPINGS_LINKED_ACCOUNTS_PATH = `${CONFIGURATION_PATH}/field-mappings/linked-accounts`;
export const CONFIGURATION_ADVANCED_FIELD_MAPPINGS_TARGET_FIELDS_PATH = `${CONFIGURATION_PATH}/field-mappings/target-fields`;
export const CONFIGURATION_ADVANCED_SELECTIVE_SYNC_PATH = `${CONFIGURATION_ADVANCED_PATH}/selective-sync`;
export const CONFIGURATION_ADVANCED_SELECTIVE_SYNC_SETTINGS_PATH = `${CONFIGURATION_ADVANCED_SELECTIVE_SYNC_PATH}/settings`;
export const CONFIGURATION_LINK_PATH = `${CONFIGURATION_PATH}/link`;
export const CONFIGURATION_LINK_ERROR_MESSAGES = `${CONFIGURATION_LINK_PATH}/error-messages`;
export const CONFIGURATION_LINK_SELECTIVE_SYNC = `${CONFIGURATION_LINK_PATH}/selective-sync`;
export const CONFIGURATION_LINK_APPEARANCE_PATH = `${CONFIGURATION_LINK_PATH}/appearance`;
export const CONFIGURATION_LINK_FEATURES_PATH = `${CONFIGURATION_LINK_PATH}/features`;
export const CONFIGURATION_LINK_CONFIGURATION_PATH = `${CONFIGURATION_LINK_PATH}/configuration`;
export const CONFIGURATION_LINK_BRANDING_PATH = `${CONFIGURATION_LINK_PATH}/branding`;
export const API_KEYS_PATH = "/keys";
export const API_KEYS_CREATE_PATH = `${API_KEYS_PATH}/create`;
export const API_KEYS_CREATE_TEST_PATH = `${API_KEYS_CREATE_PATH}/test`;
export const GET_IN_TOUCH_PATH_LOCAL = "/get-in-touch";
export const SFTP_TOGGLE_PATH = `${CONFIGURATION_PATH}/sftp-toggle`;
export const SFTP_HELP_PAGE_PATH =
  "https://help.merge.dev/en/articles/9054729-secure-file-transfer-protocol-sftp";
export const QBO_BANK_FEED_OAUTH_AUTHORIZE_PATH = "/intuit/authorize";
export const QBO_BANK_FEED_OAUTH_CALLBACK_PATH = "/intuit/callback";

// settings
export const PROFILE_PATH = "/profile";
export const SECURITY_PATH_DEPRECATED = "/security"; //deprecated
export const NOTIFICATIONS_SETTINGS_PATH = "/notifications";
export const ORGANIZATION_PATH = "/organization";
export const ORGANIZATION_MEMBERS_PATH = `${ORGANIZATION_PATH}/members`;
export const ORGANIZATION_PENDING_PATH = `${ORGANIZATION_PATH}/pending`;
export const AUDIT_TRAIL_PATH = "/audit-trail";
export const BILLING_PATH = "/billing";
export const ADD_PAYMENT_METHOD_PATH = "/add-payment-method";
export const SETTINGS_PATHS = [
  PROFILE_PATH,
  SECURITY_PATH_DEPRECATED,
  ORGANIZATION_PATH,
  AUDIT_TRAIL_PATH,
  BILLING_PATH,
  NOTIFICATIONS_SETTINGS_PATH,
];
// Tab/Route Disable Constants
export const OVERDUE_INVOICE_DISABLED_ROUTES = [
  DASHBOARD_PATH,
  ONBOARDING_PATH,
  ONBOARDING_GET_STARTED_TOGGLE_SCOPES_PATH,
  ONBOARDING_GET_STARTED_SYNC_SANDBOX_DATA_PATH,
  ONBOARDING_GET_STARTED_SEND_API_REQUEST_PATH,
  ONBOARDING_GET_STARTED_INTEGRATE_SYNCED_DATA_PATH,
  ONBOARDING_GET_STARTED_ADD_LINK_TO_APP_PATH,
  LINKED_ACCOUNTS_PATH,
  LOGS_PATH,
  ISSUES_PATH,
  API_TESTER_PATH,
  CONFIGURATION_PATH,
  PROFILE_PATH,
  SECURITY_PATH_DEPRECATED,
  ORGANIZATION_PATH,
  AUDIT_TRAIL_PATH,
  NOTIFICATIONS_SETTINGS_PATH,
];

// EXTERNAL LINKS

export const LANDING_PAGE_PATH = "https://www.merge.dev";
export const GET_IN_TOUCH_PATH = `${LANDING_PAGE_PATH}/get-in-touch/`;
export const GET_IN_TOUCH_WITH_TRACKING_PATH = `${LANDING_PAGE_PATH}/get-in-touch?utm_medium=app&utm_source=upgrade`;
export const TOS_PATH = `${LANDING_PAGE_PATH}/terms/`;
export const CHANGELOG_PATH = `${LANDING_PAGE_PATH}/changelog`;
export const PRIVACY_POLICY_PATH = `${LANDING_PAGE_PATH}/privacy-policy/`;
export const SUBSCRIBER_AGREEMENT_PATH = `${LANDING_PAGE_PATH}/subscriber-agreement/`;
export const DOCS_BASE_PATH = `${LANDING_PAGE_PATH}/docs/merge-unified`;
export const DOCS_SDK_PATH = `${DOCS_BASE_PATH}/sdk`;
export const DOCS_METADATA_PATH = `${DOCS_BASE_PATH}/platform-and-account-metadata/integration-metadata`;
export const DOCS_AUTHENTICATION_PATH = `${DOCS_BASE_PATH}/hris/merge-api-basics/authentication`;
const DOCS_WEBHOOKS_SECTION_PATH = `${DOCS_BASE_PATH}/reading-data/webhooks`;
export const DOCS_WEBHOOKS_PATH = `${DOCS_WEBHOOKS_SECTION_PATH}/overview`;
export const DOCS_WEBHOOKS_MERGE_TO_YOU_PATH = `${DOCS_WEBHOOKS_SECTION_PATH}/merge-webhooks`;
export const DOCS_WEBHOOKS_THIRD_PARTIES_TO_MERGE_PATH = `${DOCS_WEBHOOKS_SECTION_PATH}/third-party-webhooks`;

export const DOCS_LINKING_FLOW_BASE_PATH_DO_NOT_USE_ON_ITS_OWN = `${DOCS_BASE_PATH}/authentication`;
export const DOCS_LINKING_FLOW_GET_STARTED_PATH = `${DOCS_LINKING_FLOW_BASE_PATH_DO_NOT_USE_ON_ITS_OWN}/merge-link`;
export const DOCS_LINKING_FLOW_SINGLE_INTEGRATION_PATH = `${DOCS_LINKING_FLOW_BASE_PATH_DO_NOT_USE_ON_ITS_OWN}n/single-integration`;
export const DOCS_MAGIC_LINK_PATH = `${DOCS_LINKING_FLOW_BASE_PATH_DO_NOT_USE_ON_ITS_OWN}/magic-link`;
export const DOCS_FIELD_MAPPINGS_PATH = `${DOCS_BASE_PATH}/supplemental-data/field-mapping/overview/`;
export const DOCS_FIELD_MAPPINGS_TARGET_FIELDS_PATH = `${DOCS_BASE_PATH}/supplemental-data/field-mapping/target-fields/`;

export const DOCS_CUSTOM_OBJECTS_PATH = `${DOCS_BASE_PATH}/supplemental-data/custom-objects`;
export const DOCS_API_REF_CUSTOM_OBJECTS_PATH = `${DOCS_BASE_PATH}/crm/custom-objects/`;
export const DOCS_API_REF_CUSTOM_OBJECT_CLASSES_PATH = `${DOCS_BASE_PATH}/crm/custom-object-classes/`;
export const DOCS_API_REF_ASSOCIATIONS_PATH = `${DOCS_BASE_PATH}/crm/associations/`;
export const DOCS_API_REF_ASSOCIATION_TYPES_PATH = `${DOCS_BASE_PATH}/crm/association-types/`;
export const DOCS_DATA_WAREHOUSE_PATH = `${DOCS_BASE_PATH}/datawarehouse/overview`;

export const DOCS_REMOTE_DATA_PATH = `${DOCS_BASE_PATH}/supplemental-data/remote-data`;
export const DOCS_REMOTE_FIELDS_PATH = `${DOCS_BASE_PATH}/supplemental-data/remote-field-classes/remote-field-classes`;
export const DOCS_PASSTHROUGH_REQUESTS_PATH = `${DOCS_BASE_PATH}/supplemental-data/passthrough-request/overview`;

export const DOCS_WRITES_MERGE_WRITES = `${DOCS_BASE_PATH}/writing-data/writes/`;
export const DOCS_WRITES_RELATED_AND_NESTED_WRITES = `${DOCS_WRITES_MERGE_WRITES}/nested`;
export const DOCS_WRITES_PROGRAMMATIC_WRITES = `${DOCS_BASE_PATH}/writing-data/programmatic-writes-with-meta/`;
export const DOCS_WRITES_PROGRAMMATIC_NESTED_WRITES = `${DOCS_WRITES_PROGRAMMATIC_WRITES}/nested`;
export const DOCS_WRITES_TEMPLATES_AND_CONDITIONAL_FIELDS = `${DOCS_WRITES_PROGRAMMATIC_WRITES}/templates-and-conditional-fields`;

export const INITIAL_AND_ANY_SYNC_GUIDE =
  "http://help.merge.dev/en/articles/8426389-webhook-migration-guide-coming-soon";
export const MERGE_ADMIN_PATH = "https://admin.merge.dev";
export const MERGE_HELP_CENTER_PATH = "https://help.merge.dev";
export const MERGE_HOW_YOUR_INVOICES_ARE_CALCULATED =
  "https://help.merge.dev/en/articles/5518444-how-are-my-invoices-calculated";
export const MERGE_BILLING_SELF_SERVE_HELP_ARTICLE_PATH =
  "https://help.merge.dev/en/articles/6641605-self-serve-with-merge-free-and-launch-plans-faq#h_098ba7572b";

export const MERGE_BILLING_SELF_SERVE_AFTER_APRIL =
  "https://help.merge.dev/en/articles/6641605-self-serve-with-merge-free-and-launch-plans-faq";

export const MERGE_QUICKBOOKS_WEBHOOKS_HELP_CENTER =
  "https://help.merge.dev/en/articles/9527556-quickbooks-online-how-do-i-set-up-receiving-webhooks";

export const MERGE_XERO_WEBHOOKS_HELP_CENTER =
  "https://help.merge.dev/en/articles/9527604-xero-how-do-i-set-up-receiving-webhooks";

export const MERGE_USER_ROLE_HELP_CENTER =
  "https://help.merge.dev/en/articles/5389520-what-are-merge-s-role-based-access-controls-rbac";

export const MERGE_LINK_CUSTOMIZATION_HELP_CENTER =
  "https://help.merge.dev/articles/4933576994-merge-link-customization";

export const MERGE_END_USER_ORIGIN_ID_HELP_CENTER =
  "https://help.merge.dev/en/articles/8032943-end-user-origin-id-best-practices";

export const MERGE_THIRD_PARTY_COOKIES_HELP_CENTER =
  "https://help.merge.dev/en/articles/9660563-how-do-i-enable-third-party-cookies-in-my-browser";

export const MERGE_REDACT_UNMAPPED_DATA_HELP_CENTER =
  "https://help.merge.dev/en/articles/8358419-redacting-unmapped-data";

export const MERGE_COMMON_MODEL_SCOPES_HELP_CENTER =
  "https://help.merge.dev/en/articles/5950052-what-are-common-model-scopes";

export const MERGE_PRICING_PATH = `${LANDING_PAGE_PATH}/pricing`;
export const MERGE_END_USER_TERMS_PATH = `${LANDING_PAGE_PATH}/legal/end-user-terms`;

// A few categories are slugged differently in the docs than in the API, so linking
// with the raw category value 404s. Add an entry here when the docs slug diverges.
const DOCS_CATEGORY_SLUGS: Partial<Record<APICategory | UnreleasedAPICategory, string>> = {
  [APICategory.knowledgebase]: "knowledge-base",
};

export const getDocsCategorySlug = (category: APICategory | UnreleasedAPICategory) => {
  // Categories come off linked account payloads, so don't assume they arrive lowercase
  const normalizedCategory = category.toLowerCase() as APICategory | UnreleasedAPICategory;

  return DOCS_CATEGORY_SLUGS[normalizedCategory] ?? normalizedCategory;
};

export const getDocsCategoryOverviewPath = (category: APICategory | UnreleasedAPICategory) =>
  `${DOCS_BASE_PATH}/${getDocsCategorySlug(category)}/overview`;

export const getDocsIntegrationsCategoryOverviewPath = (
  category: APICategory | UnreleasedAPICategory,
) => `${DOCS_BASE_PATH}/${getDocsCategorySlug(category)}/integrations/overview`;

export const getDocsIntegrationAccessedEndpointsPath = (
  category: APICategory | UnreleasedAPICategory,
  integrationSlug: string,
) =>
  `${DOCS_BASE_PATH}/${getDocsCategorySlug(
    category,
  )}/integrations/${integrationSlug}?tab=accessed-third-party-endpoints`;

export const openInNewTab = (url: string) => {
  window.open(url, "_blank");
};

// HELPERS

export const appendQueryParamsHelper = (
  basePath: string,
  queryParams?: Partial<{ [key: string]: string }>,
) =>
  basePath +
  (queryParams
    ? "?" +
      Object.keys(queryParams)
        .map((key) => (queryParams[key] ? `${key}=${queryParams[key]}` : ""))
        .join("&")
    : "");

// A custom hook that builds on useLocation to parse
// the query string for you.
export const useQuery = () => new URLSearchParams(useLocation().search);

export const navigateHelper = (history: History, path: string, state?: navigateState) =>
  state != undefined ? history.push(path, state) : history.push(path);
// QUERY PARAM TYPES

type LogsTableQueryParams = Partial<{
  integration: string;
  end_user__organization_name: string;
  end_user__organization_id: string;
}>;

type IssuesTableQueryParams = Partial<{
  linked_account__integration__name: string;
  linked_account__end_user__organization_name: string;
}>;

type APITesterPageParams = Partial<{
  linked_account_id: string;
}>;

type LinkedAccountsTableQueryParams = Partial<{
  integration__name: string;
  complete_production_duplicates_of: string;
}>;

type SignUpQueryParams = Partial<{
  token: string;
  create_user_token: string;
  create_user_token_error: string;
  email: string;
}>;

type RefetchParams = Partial<{
  refetch: string;
  upgraded: string;
}>;
// GETS

export const getLogsFilterString = (queryParams?: LogsTableQueryParams) => {
  const rawString = `{"end_user_ids/0":{"options":${serializeFiltersForURL([
    queryParams?.end_user__organization_id || "",
    queryParams?.end_user__organization_name || "",
  ])},"comparators":${serializeFiltersForURL([
    "CONTAINS",
    "CONTAINS",
  ])}},"integration_ids/1":{"options":${serializeFiltersForURL([
    queryParams?.integration || "",
    queryParams?.integration || "",
  ])},"comparators":${serializeFiltersForURL(["CONTAINS", "CONTAINS"])}}}`;
  return compressToEncodedURIComponent(rawString);
};

export const getLogsTablePath = (queryParams?: LogsTableQueryParams) =>
  appendQueryParamsHelper(API_REQUESTS_LOGS_PATH, {
    filters: getLogsFilterString(queryParams),
  });

export const getProductionLinkedAccountsTablePath = (
  queryParams?: LinkedAccountsTableQueryParams,
) => appendQueryParamsHelper(PROD_LINKED_ACCOUNTS_PATH, queryParams);

export const getBillingPath = (queryParams?: RefetchParams) =>
  appendQueryParamsHelper(BILLING_PATH, queryParams);

export const getTestLinkedAccountsTablePath = (queryParams?: LinkedAccountsTableQueryParams) =>
  appendQueryParamsHelper(TEST_LINKED_ACCOUNTS_PATH, queryParams);

export const getIndividualIssuePath = (issueID: string) => `${ISSUES_PATH}/${issueID}`;

export const getFilteredIssuesPath = (queryParams?: IssuesTableQueryParams) =>
  appendQueryParamsHelper(ISSUES_PATH, queryParams);

export const getEditIndividualWebhookPath = (webhookID: string) =>
  `${CONFIGURATION_WEBHOOKS_EMITTERS_PATH}/${webhookID}/edit`;

export const getIssueLogEntrySidePanelPath = (issueID: string, logID: string) =>
  `${ISSUES_PATH}/${issueID}/log-entry/${logID}`;

export const getSignUpPath = (queryParams?: SignUpQueryParams) =>
  appendQueryParamsHelper(SIGNUP_PATH, queryParams);

export const getIntegrationConfigurationPathForIndividualCategory = (
  category: APICategory | UnreleasedAPICategory,
) => `${CONFIGURATION_INTEGRATIONS_PATH}/${category.toLowerCase()}`;

export const commonModelsConfigurationPathForIndividualCategory = (
  category: APICategory | UnreleasedAPICategory,
) => `${CONFIGURATION_COMMON_MODELS_PATH}/${category.toLowerCase()}`;

export const getCustomFieldConfigurationPathForIndividualCategory = (
  category: APICategory | UnreleasedAPICategory,
) => `${CONFIGURATION_FIELD_MAPPINGS_PATH}/${category.toLowerCase()}`;

export const apiDocsPathForCategory = (category: APICategory | UnreleasedAPICategory) =>
  `${DOCS_BASE_PATH}/${getDocsCategorySlug(category)}`;

export const getLinkedAccountDetailPathForId = (id: string) =>
  `${LINKED_ACCOUNT_DETAIL_PATH}/${id}`;

export const getAPITesterPathForLinkedAccountMergeToThirdParty = (
  queryParams?: APITesterPageParams,
) => appendQueryParamsHelper(API_TESTER_MERGE_TO_THIRD_PARTY_PATH, queryParams);

export const getAPITesterPathForLinkedAccountYouToMerge = (queryParams?: APITesterPageParams) =>
  appendQueryParamsHelper(API_TESTER_YOU_TO_MERGE_LINKED_ACCOUNT_PATH, queryParams);

export const getLinkedAccountDetailOverviewPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}/overview`;

export const getLinkedAccountApiRequestLogsPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}/logs/api-requests`;

export const getLinkedAccountWebhookLogsPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}/logs/webhooks`;

export const getCreateCommonModelFieldMappingForID = (
  id: string,
  common_model: string,
  queryParams: {
    fieldMappingTargetID?: string;
    fieldMappingInstanceID?: string;
  },
) =>
  appendQueryParamsHelper(
    `${getLinkedAccountDetailPathForId(id)}/custom-mappings/create/${common_model}`,
    queryParams,
  );

export const getLinkedAccountSelectiveSyncPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}/selective-sync`;

export const getLinkedAccountDetailWebhooksPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}/webhooks`;

export const getLinkedAccountDetailAPIRequestLogsSettingsPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}${API_REQUESTS_LOGS_PATH}`;

export const getLinkedAccountDetailWebhookLogsSettingsPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}${WEBHOOKS_LOGS_PATH}`;

export const getLinkedAccountDetailLogsSettingsPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}${LOGS_PATH}`;

export const getLinkedAccountDetailFieldMappingsPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}/custom-mappings`;

export const getLinkedAccountUnmappedTargetsPath = (id: string, commonModelName?: string | null) =>
  `${getLinkedAccountDetailPathForId(id)}/custom-mappings/unmapped-targets${
    commonModelName ? `?commonModelName=${commonModelName}` : ""
  }`;
export const getLinkedAccountIssuesPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}/issues`;

export const getLinkedAccountDetailSettingsPathForId = (id: string) =>
  `${getLinkedAccountDetailPathForId(id)}/settings`;

export const getFieldMappingSettingsPathForId = (fieldMappingTargetId: string) => {
  return `${CONFIGURATION_ADVANCED_FIELD_MAPPINGS_TARGET_FIELDS_PATH}/${fieldMappingTargetId}/settings`;
};

export const getCreatedIntegrationWideFieldMapping = (
  fieldMappingTargetId: string,
  integrationId: string,
) => {
  return `${CONFIGURATION_ADVANCED_FIELD_MAPPINGS_TARGET_FIELDS_PATH}/${fieldMappingTargetId}/${integrationId}/create`;
};

export const getIndividualCommonModelOverrideTargetPath = (id: string) =>
  `${CONFIGURATION_ADVANCED_FIELD_MAPPINGS_TARGET_FIELDS_PATH}/common-model-overrides/${id}/settings`;
// NAVIGATES

export const navigateToHomePage = (history: History) => navigateHelper(history, ROOT_PATH);

export const navigateToSignUpPage = (history: History, queryParams?: SignUpQueryParams) =>
  navigateHelper(history, getSignUpPath(queryParams));

export const navigateToFieldMappings = (history: History) =>
  navigateHelper(history, CONFIGURATION_ADVANCED_FIELD_MAPPINGS_TARGET_FIELDS_PATH);

export const navigateToLinkedAccountFieldMappings = (history: History, linkedAccountID: string) =>
  navigateHelper(history, getLinkedAccountDetailFieldMappingsPathForId(linkedAccountID));

export const navigateToFieldMappingSettings = (history: History, targetFieldID: any) =>
  navigateHelper(history, getFieldMappingSettingsPathForId(targetFieldID));

export const navigateToConfigurationFieldMappingTargets = (history: History) =>
  navigateHelper(history, CONFIGURATION_FIELD_MAPPINGS_TARGET_FIELDS_PATH);

export const navigateToCreateCommonModelFieldMapping = (
  history: History,
  linkedAccountID: string,
  commonModel: string,
  existingTargetInformation: {
    fieldMappingTargetID?: string;
    fieldMappingInstanceID?: string;
  },
) =>
  navigateHelper(
    history,
    getCreateCommonModelFieldMappingForID(linkedAccountID, commonModel, existingTargetInformation),
  );

export const navigateToAddIntegrationFieldMapping = (
  history: History,
  fieldMappingTargetID: string,
  integrationID: string,
) => {
  const path = getCreatedIntegrationWideFieldMapping(fieldMappingTargetID, integrationID);
  navigateHelper(history, path);
};
export const navigateToWebhooksConfigurationPage = (history: History) =>
  navigateHelper(history, CONFIGURATION_WEBHOOKS_PATH);

export const navigateToAddWebhookPage = (history: History) =>
  navigateHelper(history, CONFIGURATION_WEBHOOKS_EMITTERS_ADD_PATH);

export const navigateToToggleScopes = (history: History) =>
  navigateHelper(history, ONBOARDING_GET_STARTED_TOGGLE_SCOPES_PATH);

export const navigateToSyncSandboxData = (history: History) =>
  navigateHelper(history, ONBOARDING_GET_STARTED_SYNC_SANDBOX_DATA_PATH);

export const navigateToSendAPIRequest = (history: History) =>
  navigateHelper(history, ONBOARDING_GET_STARTED_SEND_API_REQUEST_PATH);

export const navigateToIntegrateSyncedData = (history: History) =>
  navigateHelper(history, ONBOARDING_GET_STARTED_INTEGRATE_SYNCED_DATA_PATH);

export const navigateToConfigureIntegrationsPage = (history: History, category: APICategory) =>
  navigateHelper(history, getIntegrationConfigurationPathForIndividualCategory(category));

export const navigateToAddLinkToApp = (history: History) =>
  navigateHelper(history, ONBOARDING_GET_STARTED_ADD_LINK_TO_APP_PATH);

export const navigateToOnboardingSyncingData = (history: History) =>
  navigateHelper(history, ONBOARDING_ADVANCED_SYNCING_DATA_PATH);

export const navigateToOnboardingMergeWrites = (history: History) =>
  navigateHelper(history, ONBOARDING_ADVANCED_MERGE_WRITES_PATH);

export const navigateToOnboardingWebhooks = (history: History) =>
  navigateHelper(history, ONBOARDING_ADVANCED_WEBHOOKS_PATH);

export const navigateToOnboardingMetadata = (history: History) =>
  navigateHelper(history, ONBOARDING_ADVANCED_METADATA_PATH);

export const navigateToOnboardingSupplementalData = (history: History) =>
  navigateHelper(history, ONBOARDING_ADVANCED_SUPPLEMENTAL_DATA_PATH);

export const navigateToAISkills = (history: History) =>
  navigateHelper(history, ONBOARDING_AI_SKILLS_PATH);

export const navigateToIssuesPage = (history: History) => navigateHelper(history, ISSUES_PATH);

export const navigateToIndividualIssuePage = (
  history: History,
  issueID: string,
  prevPage?: string,
) =>
  history.push(getIndividualIssuePath(issueID), {
    from: prevPage ? prevPage : null,
  });

export const navigateToLogsTablePage = (history: History, queryParams?: LogsTableQueryParams) =>
  navigateHelper(history, getLogsTablePath(queryParams));

// Use redirect from LINKED_ACCOUNTS_PATH to PROD_LINKED_ACCOUNTS_PATH to clear query params
export const navigateToLinkedAccountsTablePage = (history: History) =>
  navigateHelper(history, LINKED_ACCOUNTS_PATH);

export const navigateToLinkedAccountIssuesPage = (history: History, account: LinkedAccount) =>
  navigateHelper(history, getLinkedAccountIssuesPathForId(account.id));

export const navigateToLinkedAccountLogsPage = (history: History, account: LinkedAccount) =>
  navigateHelper(history, getLinkedAccountApiRequestLogsPathForId(account.id));

export const navigateToContactPage = (history: History) => {
  navigateHelper(history, GET_IN_TOUCH_PATH);
};

export const navigateToLinkedAccountLogsPageById = (
  history: History,
  linkedAccountID: string,
  logID: string,
) => {
  const logPath = `${getLinkedAccountApiRequestLogsPathForId(linkedAccountID)}/${logID}`;
  navigateHelper(history, logPath);
};

export const navigateToProductionLinkedAccountsTablePage = (
  history: History,
  queryParams?: LinkedAccountsTableQueryParams,
) => navigateHelper(history, getProductionLinkedAccountsTablePath(queryParams));

export const navigateToTestLinkedAccountsTablePage = (
  history: History,
  queryParams?: LinkedAccountsTableQueryParams,
) => navigateHelper(history, getTestLinkedAccountsTablePath(queryParams));

export const navigateToAddPaymentPage = (
  history: History,
  state?: { fromUpgradeButton?: boolean },
) => {
  navigateHelper(history, ADD_PAYMENT_METHOD_PATH, state);
};

export const navigateToLinkedAccountDetailPage = (history: History, account: LinkedAccount) =>
  navigateHelper(history, getLinkedAccountDetailOverviewPathForId(account.id));

export const navigateToLinkedAccountDetailPageByID = (history: History, linkedAccountID: string) =>
  navigateHelper(history, getLinkedAccountDetailOverviewPathForId(linkedAccountID));

export const navigateToEditIndividualWebhookPage = (history: History, webhookID: string) =>
  navigateHelper(history, getEditIndividualWebhookPath(webhookID));

export const navigateToIssueLogEntrySidePanel = (
  history: History,
  issueID: string,
  logID: string,
) => history.push(getIssueLogEntrySidePanelPath(issueID, logID), history.location.state);

export const navigateToLoginPage = (history: History) => navigateHelper(history, LOGIN_PATH);

export const navigateToLogsPageById = (history: History, logId: string) => {
  navigateHelper(history, `${API_REQUESTS_LOGS_PATH}/${logId}`);
};

export const navigateToBillingPage = (history: History, queryParams?: RefetchParams) => {
  navigateHelper(history, getBillingPath(queryParams));
};

export const navigateToAPITesterWithLinkedAccountMergeToThirdParty = (
  history: History,
  linkedAccount: APIRequestLogEntryLinkedAccount | LinkedAccount | null,
) => {
  navigateHelper(
    history,
    getAPITesterPathForLinkedAccountMergeToThirdParty({
      linked_account_id: linkedAccount?.id ?? "",
    }),
  );
};

export const navigateToAPITesterWithLinkedAccountYouToMerge = (
  history: History,
  linkedAccount: APIRequestLogEntryLinkedAccount | LinkedAccount | null,
) => {
  navigateHelper(
    history,
    getAPITesterPathForLinkedAccountYouToMerge({
      linked_account_id: linkedAccount?.id ?? "",
    }),
  );
};

export const navigateToFailedLinkedAccountLogsPage = (
  history: History,
  linkedAccountId: string,
) => {
  const rawString = `{"response_codes/0": {"options": [["201",{"displayName": 201,"isEnabled": true}],["200",{"displayName": 200,"isEnabled": true}]],"comparators": [["NOT_EQUAL",{"displayName": "NOT_EQUAL","isEnabled": true
}]]}}`;

  const filteredUrl = appendQueryParamsHelper(
    getLinkedAccountDetailAPIRequestLogsSettingsPathForId(linkedAccountId),
    { filters: compressToEncodedURIComponent(rawString) },
  );

  navigateHelper(history, filteredUrl);
};

export const navigateToLinkedAccountDetailWebhooksPathForId = (
  history: History,
  linkedAccountId: string,
) => {
  navigateHelper(history, getLinkedAccountDetailWebhooksPathForId(linkedAccountId));
};

export const navigateToLinkedAccountDetailSelectiveSyncPathForId = (
  history: History,
  linkedAccountId: string,
) => {
  navigateHelper(history, getLinkedAccountSelectiveSyncPathForId(linkedAccountId));
};

export const navigateToIndividualCommonModelOverrideTargetPage = (
  history: History,
  commonModelOverrideTargetID: string,
) => {
  navigateHelper(
    history,
    `${CONFIGURATION_FIELD_MAPPINGS_PATH}/common-model-overrides/${commonModelOverrideTargetID}`,
  );
};

export const navigateToLinkedAccountUnmappedTargetsPage = (
  history: History,
  linkedAccountId: string,
) => {
  navigateHelper(history, getLinkedAccountUnmappedTargetsPath(linkedAccountId));
};
